import { Link } from "@/lib/router-compat";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User } from "lucide-react";
import { formatGermanDate } from "@/lib/dateFormat";
import { OptimizedImage } from "./OptimizedImage";

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt?: string;
  featuredImage?: string;
  categoryName?: string;
  author?: string;
  publishedAt?: string;
  readingTime?: number;
}

export const ArticleCard = ({
  slug,
  title,
  excerpt,
  featuredImage,
  categoryName,
  author = "Redaktion",
  publishedAt,
  readingTime,
}: ArticleCardProps) => {
  return (
    <Link to={`/ratgeber/${slug}`} className="group block">
      <article className="h-full overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-lg">
        {/* Featured Image */}
        {featuredImage && (
          <OptimizedImage
            src={featuredImage}
            alt={title}
            width={640}
            height={360}
            containerClassName="aspect-video w-full"
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        )}

        <div className="p-6">
          {/* Category Badge */}
          {categoryName && (
            <Badge variant="secondary" className="mb-3">
              {categoryName}
            </Badge>
          )}

          {/* Title */}
          <h3 className="mb-3 text-xl font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </h3>

          {/* Excerpt */}
          {excerpt && (
            <p className="mb-4 text-sm text-muted-foreground line-clamp-3">
              {excerpt}
            </p>
          )}

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            {author && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{author}</span>
              </div>
            )}
            {publishedAt && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatGermanDate(publishedAt, 'dd.MM.yyyy')}</span>
              </div>
            )}
            {readingTime && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{readingTime} {readingTime === 1 ? 'Minute' : 'Minuten'}</span>
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
};
